/**
 * Résolution des retours contextuels (`?from=`) des pages de détail.
 *
 * Les query params arrivent bruts du client : on ne les réfléchit JAMAIS tels
 * quels dans un href (anti open-redirect). Chaque contexte est résolu via une
 * whitelist, et les identifiants (équipe, email) sont validés puis ré-encodés.
 */

// ─── Activation L16 (MASTER-GEST-PROFILE) ────────────────────────────────────
// Ce module etait porte mais SANS AUCUN consommateur : /profile est son premier
// (F-813). Deux adaptations au parc, faites ICI plutot que dupliquees au callsite :
//
//  1. `labelKey` (cle i18n) au lieu du `label` FR en dur du PoC, sur
//     resolveProfileBack — le parc est bilingue natif (RÈGLES I18N).
//  2. WHITELIST REDUITE AUX ROUTES QUI EXISTENT : le PoC whitelistait aussi
//     `mon-equipe`, `admin`, `admin-remuneration` et `grille`. Ces ecrans ne sont
//     PAS portes (L09 / admin / grille salariale) : les garder produirait des
//     liens « Retour » vers des 404 — pire qu'un BackLink absent. Ils retombent
//     donc sur `default: null`, qui est deja le comportement PoC pour l'inconnu.
//     A re-ouvrir au fur et a mesure que ces ecrans arrivent.
//
// resolvePlanningBack est laisse INTACT (hors perimetre L16, toujours sans
// consommateur — /planification-salariale n'est pas porte non plus).

export type BackTarget = { href: string; label: string }

/** Cible de retour du profil (F-813) — `labelKey` = cle i18n, jamais un libelle. */
export type ProfileBackTarget = { href: string; labelKey: string }

const TEAM_ID_RE = /^[A-Za-z0-9_-]+$/
const EMAIL_RE = /^[^@\s]+@[^@\s]+$/

function safeTeamId(raw: string | undefined | null): string | null {
  return raw && TEAM_ID_RE.test(raw) ? raw : null
}

function safeEmail(raw: string | undefined): string | null {
  const email = raw?.toLowerCase().trim()
  return email && EMAIL_RE.test(email) ? email : null
}

/** F-813 : retour depuis le profil membre (`/profile/:email?from=…&fromTeam=…`). */
export function resolveProfileBack(
  from?: string | null,
  fromTeam?: string | null
): ProfileBackTarget | null {
  const teamId = safeTeamId(fromTeam)
  switch (from) {
    case 'organigramme':
      return { href: '/organigramme', labelKey: 'gest:profile.back.organigramme' }
    case 'equipes':
      return teamId
        // teamId valide par TEAM_ID_RE PUIS re-encode : aucune valeur brute du
        // client n'atteint l'href.
        ? { href: `/equipes/${encodeURIComponent(teamId)}`, labelKey: 'gest:profile.back.team' }
        : { href: '/equipes', labelKey: 'gest:profile.back.teams' }
    default:
      // Inclut les origines PoC non portees (mon-equipe/admin/grille) — voir
      // l'en-tete : mieux vaut aucun BackLink qu'un lien vers un 404.
      return null
  }
}

/** Retour depuis la planification salariale (`/planification-salariale?from=…`). */
export function resolvePlanningBack({
  from,
  member,
  team,
}: {
  from?: string
  member?: string
  team?: string
}): BackTarget | null {
  switch (from) {
    case 'mon-equipe': {
      const teamId = safeTeamId(team)
      return {
        href: teamId ? `/mon-equipe?team=${teamId}` : '/mon-equipe',
        label: 'Mon équipe',
      }
    }
    case 'profile': {
      const email = safeEmail(member)
      return email
        ? {
            href: `/profile/${encodeURIComponent(email)}?tab=gestion`,
            label: 'Profil membre',
          }
        : null
    }
    default:
      return null
  }
}

/**
 * Filtre les params de contexte à re-propager dans les hrefs (onglets du
 * profil) : seules les valeurs qui résolvent vers un retour valide passent.
 */
export function profileBackQuery(
  from?: string | null,
  fromTeam?: string | null
): Record<string, string> | undefined {
  if (!resolveProfileBack(from, fromTeam)) return undefined
  return {
    from: from as string,
    ...(safeTeamId(fromTeam) ? { fromTeam: fromTeam as string } : {}),
  }
}
